import React, { useState, useEffect } from "react";
import { X, Loader2, Info } from "lucide-react";
import {
  Tab,
  Vacature,
  CreateVacatureRequest,
  UpdateVacatureRequest,
  ExperienceLevel,
  Discipline,
  EXPERIENCE_LABELS,
  DISCIPLINE_LABELS,
} from "../types";

interface VacatureModalProps {
  mode: "create" | "edit";
  partnerId: number;
  tabs: Tab[];
  activeTabId?: number;
  vacature?: Vacature; // for edit mode
  onSubmit: (data: CreateVacatureRequest | UpdateVacatureRequest) => Promise<void>;
  onClose: () => void;
}

const prioriteitOpties = [
  { value: "hoog", label: "Hoog" },
  { value: "normaal", label: "Normaal" },
  { value: "laag", label: "Laag" },
];

const statusOpties = [
  { value: "open", label: "Open" },
  { value: "gesloten", label: "Gesloten" },
  { value: "on_hold", label: "On Hold" },
];

const bemiddelingOpties = [
  { value: "geen", label: "Geen" },
  { value: "in_bemiddeling", label: "In Bemiddeling" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "geplaatst", label: "Geplaatst" },
];

const experienceOpties: { value: ExperienceLevel; label: string }[] = [
  { value: "nvt", label: EXPERIENCE_LABELS.nvt },
  { value: "starter", label: EXPERIENCE_LABELS.starter },
  { value: "1_3", label: EXPERIENCE_LABELS["1_3"] },
  { value: "3_5", label: EXPERIENCE_LABELS["3_5"] },
  { value: "5_10", label: EXPERIENCE_LABELS["5_10"] },
  { value: "10_plus", label: EXPERIENCE_LABELS["10_plus"] },
];

const disciplineOpties: { value: Discipline; label: string }[] = [
  { value: "nvt", label: DISCIPLINE_LABELS.nvt },
  { value: "combi", label: DISCIPLINE_LABELS.combi },
  { value: "water", label: DISCIPLINE_LABELS.water },
  { value: "elektra_laag", label: DISCIPLINE_LABELS.elektra_laag },
  { value: "elektra_hoog", label: DISCIPLINE_LABELS.elektra_hoog },
  { value: "gas", label: DISCIPLINE_LABELS.gas },
  { value: "riool", label: DISCIPLINE_LABELS.riool },
  { value: "overig", label: DISCIPLINE_LABELS.overig },
];

export default function VacatureModal({
  mode,
  partnerId,
  tabs,
  activeTabId,
  vacature,
  onSubmit,
  onClose,
}: VacatureModalProps) {
  const [functie, setFunctie] = useState(vacature?.functie ?? "");
  const [tabId, setTabId] = useState<number>(
    vacature?.tab_id ?? activeTabId ?? tabs[0]?.id ?? 0
  );
  const [locatie, setLocatie] = useState(vacature?.locatie ?? "");
  const [priority, setPriority] = useState<"hoog" | "normaal" | "laag">(vacature?.priority ?? "normaal");
  const [status, setStatus] = useState<"open" | "gesloten" | "on_hold">(vacature?.status ?? "open");
  const [contactpersoon, setContactpersoon] = useState(vacature?.contactpersoon ?? "");
  const [aantalPosties, setAantalPosities] = useState(vacature?.aantal_posities ?? 1);
  const [bemiddelingStatus, setBemiddelingStatus] = useState<"geen" | "in_bemiddeling" | "shortlisted" | "geplaatst">(
    vacature?.bemiddeling_status ?? "geen"
  );
  const [opmerkingen, setOpmerkingen] = useState(vacature?.opmerkingen ?? "");
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel>(
    vacature?.experience_level ?? "nvt"
  );
  const [discipline, setDiscipline] = useState<Discipline>(vacature?.discipline ?? "nvt");
  const [disciplineOverigText, setDisciplineOverigText] = useState(
    vacature?.discipline_overig_text ?? ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Determine if selected tab differs from active tab
  const selectedTab = tabs.find((t) => t.id === tabId);
  const showTabHint =
    mode === "create" &&
    activeTabId !== undefined &&
    tabId !== activeTabId &&
    selectedTab;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!functie.trim()) {
      setError("Functie is verplicht.");
      return;
    }
    if (!tabId) {
      setError("Selecteer een locatie/tab.");
      return;
    }
    if (discipline === "overig" && !disciplineOverigText.trim()) {
      setError("Vul een omschrijving in voor 'Overig'.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      if (mode === "create") {
        const data: CreateVacatureRequest = {
          functie: functie.trim(),
          partner_id: partnerId,
          tab_id: tabId,
          priority,
          contactpersoon: contactpersoon.trim() || undefined,
          locatie: locatie.trim() || undefined,
          aantal_posities: aantalPosties,
          opmerkingen: opmerkingen.trim() || undefined,
          experience_level: experienceLevel,
          discipline,
          discipline_overig_text:
            discipline === "overig" ? disciplineOverigText.trim() : null,
        };
        await onSubmit(data);
      } else {
        const data: UpdateVacatureRequest = {
          functie: functie.trim(),
          status,
          priority,
          contactpersoon: contactpersoon.trim() || undefined,
          locatie: locatie.trim() || undefined,
          aantal_posities: aantalPosties,
          bemiddeling_status: bemiddelingStatus,
          opmerkingen: opmerkingen.trim() || undefined,
          tab_id: tabId,
          experience_level: experienceLevel,
          discipline,
          discipline_overig_text:
            discipline === "overig" ? disciplineOverigText.trim() : null,
        };
        await onSubmit(data);
      }
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Er is een fout opgetreden.");
    } finally {
      setLoading(false);
    }
  }

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 md:mx-auto max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">
            {mode === "create" ? "Nieuwe Vacature" : "Vacature Bewerken"}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Functie */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Functie <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={functie}
              onChange={(e) => setFunctie(e.target.value)}
              placeholder="Bijv. Projectleider Infra"
              className="input-field"
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Locatie / Tab */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Vestiging / Tab <span className="text-red-500">*</span>
            </label>
            <select
              value={tabId}
              onChange={(e) => setTabId(Number(e.target.value))}
              className="select-field"
              disabled={loading}
            >
              {tabs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {showTabHint && (
              <div className="mt-2 flex items-start gap-2 bg-orange-50 border border-orange-200 text-orange-700 rounded-lg px-3 py-2 text-xs">
                <Info size={13} className="flex-shrink-0 mt-0.5" />
                Verschijnt in tab <strong className="ml-1">{selectedTab.name}</strong>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Prioriteit */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Prioriteit
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as "hoog" | "normaal" | "laag")}
                className="select-field"
                disabled={loading}
              >
                {prioriteitOpties.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Aantal posities */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Aantal Posities
              </label>
              <input
                type="number"
                min={1}
                max={99}
                value={aantalPosties}
                onChange={(e) => setAantalPosities(Number(e.target.value))}
                className="input-field"
                disabled={loading}
              />
            </div>
          </div>

          {/* Status (edit only) */}
          {mode === "edit" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as "open" | "gesloten" | "on_hold")}
                  className="select-field"
                  disabled={loading}
                >
                  {statusOpties.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Bemiddeling
                </label>
                <select
                  value={bemiddelingStatus}
                  onChange={(e) => setBemiddelingStatus(e.target.value as "geen" | "in_bemiddeling" | "shortlisted" | "geplaatst")}
                  className="select-field"
                  disabled={loading}
                >
                  {bemiddelingOpties.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Ervaringsniveau + Discipline */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Ervaringsniveau
              </label>
              <select
                value={experienceLevel}
                onChange={(e) => setExperienceLevel(e.target.value as ExperienceLevel)}
                className="select-field"
                disabled={loading}
              >
                {experienceOpties.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Discipline
              </label>
              <select
                value={discipline}
                onChange={(e) => setDiscipline(e.target.value as Discipline)}
                className="select-field"
                disabled={loading}
              >
                {disciplineOpties.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {discipline === "overig" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Discipline omschrijving <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={disciplineOverigText}
                onChange={(e) => setDisciplineOverigText(e.target.value)}
                placeholder="Bijv. Telecom, Datakabels…"
                className="input-field"
                disabled={loading}
              />
            </div>
          )}

          {/* Contactpersoon */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Contactpersoon
            </label>
            <input
              type="text"
              value={contactpersoon}
              onChange={(e) => setContactpersoon(e.target.value)}
              placeholder="Naam contactpersoon"
              className="input-field"
              disabled={loading}
            />
          </div>

          {/* Locatie (free text) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Locatie (vrij tekstveld)
            </label>
            <input
              type="text"
              value={locatie}
              onChange={(e) => setLocatie(e.target.value)}
              placeholder="Bijv. Utrecht, Randstad"
              className="input-field"
              disabled={loading}
            />
          </div>

          {/* Opmerkingen */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Opmerkingen
            </label>
            <textarea
              value={opmerkingen}
              onChange={(e) => setOpmerkingen(e.target.value)}
              placeholder="Aanvullende informatie…"
              rows={3}
              className="input-field resize-none"
              disabled={loading}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary min-h-[44px] sm:min-h-0"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Opslaan…
                </>
              ) : mode === "create" ? (
                "Vacature Aanmaken"
              ) : (
                "Wijzigingen Opslaan"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
